import React from 'react';
import { Heart, Users, BookOpen, Home } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from './shared/SEO';
import PageLayout from './layout/PageLayout';
import CTASection from './shared/CTASection';

const NeverlandPage = () => {
  const neverlandSchema = {
    "@context": "https://schema.org",
    "@type": "Program",
    "name": "Neverland Initiative",
    "provider": {
      "@type": "Organization",
      "name": "COTAD",
      "nonprofitStatus": "501(c)(3)"
    },
    "description": "Support program for youth aged 11-30 providing counseling, education, and community support"
  };

  const programFeatures = [
    {
      icon: Heart,
      title: "Safe Space",
      description: "A welcoming environment where you can be yourself without judgment",
      gradient: "from-purple-600 to-blue-600"
    },
    {
      icon: Users,
      title: "Community Support",
      description: "Connect with peers and mentors who understand your journey",
      gradient: "from-blue-600 to-cyan-500"
    },
    {
      icon: BookOpen,
      title: "Education & Growth",
      description: "Access to educational resources and personal development opportunities",
      gradient: "from-cyan-500 to-green-500"
    },
    {
      icon: Home,
      title: "Housing Assistance",
      description: "Support finding safe and stable housing options",
      gradient: "from-orange-500 to-amber-500"
    }
  ];

  return (
    <>
      <SEO
        title="Neverland Initiative | Youth Support Program at COTAD"
        description="The Neverland Initiative provides comprehensive support, counseling, and community for youth aged 11-30 who need a safe space to grow and thrive."
        pathname="/neverland"
      >
        <script type="application/ld+json">
          {JSON.stringify(neverlandSchema)}
        </script>
      </SEO>

      <PageLayout
        title="The Neverland Initiative"
        description="A safe haven for youth to grow, learn, and thrive"
      >
        {/* Main Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="prose prose-lg">
                <p>
                  The Neverland Initiative is our flagship program designed to support youth aged 11-30 who have faced rejection, displacement, or adversity. We provide a comprehensive support system that nurtures spiritual growth while offering practical assistance and emotional support.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Program Features */}
        <section className="py-16 bg-bg-secondary">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {programFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`feature-card neverland-feature-item`}
                >
                  <div className={`feature-icon bg-gradient-to-r ${feature.gradient}`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mt-4">{feature.title}</h3>
                  <p className="text-text-secondary mt-2">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-semibold mb-6">Who Can Join?</h2>
              <p className="text-lg text-text-secondary">
                The Neverland Initiative welcomes all youth aged 11-30, regardless of background, beliefs, or circumstances. Whether you need emergency support, ongoing assistance, or simply want to be part of a caring community, our doors are open to you.
              </p>
            </div>
          </div>
        </section>

        <CTASection
          variant="support"
          title="Ready to take the first step?"
          description="Contact us to learn more about the Neverland Initiative and how we can support you."
        />
      </PageLayout>
    </>
  );
};

export default NeverlandPage;